import { FormCombobox, FormInput, FormSelect } from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  CITY,
  EMAIL,
  GEOGRAPHICAL_REGION,
  MOBILE,
  NAME,
  ROLE,
  STATE,
} from "../consts";
import { X } from "lucide-react";
import { capitalize } from "@/lib/utils";
import { roles } from "../consts";

const AddEdit = () => {
  const {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel,
    isMobile,
    action,
    isEdit,
    isLoading,
    geographicalRegionOptions,
    stateOptions,
    cityOptions,
    isLoadingGeoRegions,
    geoRegionsError,
    isLoadingEmployee,
    employeeError,
  } = useAddEdit();
  const { errors } = form.formState;

  // Show error if geographical regions fail to load
  if (geoRegionsError && !isView) {
    return (
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <DrawerHeader className="px-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-[20px]">
                {isEdit ? "Update" : capitalize(action || "")} Employee
              </DrawerTitle>
              <X
                onClick={onCancel}
                className="cursor-pointer stroke-g-300 hover:stroke-g-400"
              />
            </div>
          </DrawerHeader>
          <div className="p-6 text-center">
            <p className="text-red-600">
              Failed to load geographical regions. Please try again.
            </p>
            <Button onClick={onCancel} className="mt-4">
              Close
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Show error if employee data fails to load when editing
  if (isEdit && employeeError && !isView) {
    return (
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <DrawerHeader className="px-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-[20px]">Update Employee</DrawerTitle>
              <X
                onClick={onCancel}
                className="cursor-pointer stroke-g-300 hover:stroke-g-400"
              />
            </div>
          </DrawerHeader>
          <div className="p-6 text-center">
            <p className="text-red-600">
              Failed to load employee data. Please try again.
            </p>
            <Button onClick={onCancel} className="mt-4">
              Close
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Show loading state when fetching employee data for editing
  if (isEdit && isLoadingEmployee) {
    return (
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <DrawerHeader className="px-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-[20px]">Update Employee</DrawerTitle>
              <X
                onClick={onCancel}
                className="cursor-pointer stroke-g-300 hover:stroke-g-400"
              />
            </div>
          </DrawerHeader>
          <div className="p-6 text-center">
            <p>Loading employee data...</p>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {isEdit ? "Update" : capitalize(action || "")} Employee
            </DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[100px] px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px]">
                <FormInput
                  name={NAME}
                  control={form.control}
                  label="Employee Name"
                  placeholder="Please enter"
                  error={errors[NAME]?.message}
                  isRequired={requiredFields.includes(NAME)}
                  disabled={isView}
                />
                <FormInput
                  name={EMAIL}
                  control={form.control}
                  label={`Email ID${isEdit ? " (Read-only)" : ""}`}
                  placeholder="Please enter"
                  error={errors[EMAIL]?.message}
                  isRequired={requiredFields.includes(EMAIL)}
                  disabled={isView || isEdit} // Disable email field when editing
                />
                {isEdit && (
                  <div className="col-span-2 -mt-5 mb-2">
                    <p className="text-sm text-gray-500 italic">
                      Note: Email address cannot be changed for security reasons
                    </p>
                  </div>
                )}
                <FormInput
                  name={MOBILE}
                  control={form.control}
                  label="Mobile No. "
                  placeholder="Please enter"
                  error={errors[MOBILE]?.message}
                  isRequired={requiredFields.includes(MOBILE)}
                  disabled={isView}
                />
                <FormCombobox
                  name={ROLE}
                  control={form.control}
                  label="Role"
                  placeholder="Please select"
                  error={errors[ROLE]?.message}
                  isRequired={requiredFields.includes(ROLE)}
                  disabled={isView}
                  options={roles}
                  multiSelect
                />
                <FormSelect
                  name={GEOGRAPHICAL_REGION}
                  control={form.control}
                  label="Geographical Region"
                  placeholder={
                    isLoadingGeoRegions ? "Loading..." : "Please select"
                  }
                  error={errors[GEOGRAPHICAL_REGION]?.message}
                  isRequired={requiredFields.includes(GEOGRAPHICAL_REGION)}
                  disabled={isView || isLoadingGeoRegions}
                  options={geographicalRegionOptions}
                />
                <FormSelect
                  name={STATE}
                  control={form.control}
                  label="State"
                  placeholder="Please select"
                  error={errors[STATE]?.message}
                  isRequired={requiredFields.includes(STATE)}
                  disabled={isView}
                  options={stateOptions}
                />
                <FormSelect
                  name={CITY}
                  control={form.control}
                  label="City"
                  placeholder="Please select"
                  error={errors[CITY]?.message}
                  isRequired={requiredFields.includes(CITY)}
                  disabled={isView}
                  options={cityOptions}
                />
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-0 w-full bg-white pb-[25px] z-[3]">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              {isView ? (
                <></>
              ) : (
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Submitting..." : isEdit ? "Update" : "Submit"}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddEdit;
